import { Component, Inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ClientUser, UserInvite } from '../../../models/client-user-management';
import { Pagination } from '../../../models/pagination';
import { ModalService } from '../../../services/modal.service';
import { UserManagementService } from '../../../services/user-management.service';
import { InstanceTypes } from '../../../models/instance-types';
import { AuthService } from '../../../services/auth.service';
import { concat, forkJoin, Observable } from 'rxjs';
import { ModalStates } from '../../../models/modal';
import { RelayNotificationsService } from '../../../services/relay-notifications.service';
import { CompaniesService } from '../../../services/companies.service';
import { Client } from '../../../models/client';
import { AclService } from '../../../services/acl.service';
import { FlattenResponseMessages } from '../../../utils/api-response-helper';
import { Store } from '@ngxs/store';
import { ClientSelectionState } from '../../../state/ClientSelectionState/ClientSelection.state';

@Component({
  selector: 'app-client-user-management',
  templateUrl: './client-user-management.component.html',
  styleUrls: ['./client-user-management.component.scss'],
  standalone: false
})
export class ClientUserManagementComponent implements OnInit {
  public clientUsers: ClientUser[] = [];
  public showInviteModal = false;
  public searchQuery: string | null;
  public inviteModel: ClientUser | null;
  public searchResults = [];
  public pagination: Pagination = { Page: 1, Pages: 1, PerPage: 25, Total: 0 };
  public get isClientPortal() {
    return this.env.instanceType === InstanceTypes.ClientPortal;
  }
  public clientCode: string;
  private isEdit = false;
  private company: Client | null;
  private isCsr = false;

  constructor(
    private userService: UserManagementService,
    private activeRoute: ActivatedRoute,
    private modalService: ModalService,
    private authService: AuthService,
    private notificationService: RelayNotificationsService,
    private companyService: CompaniesService,
    private aclService: AclService,
    @Inject('env') private env: any,
    private store: Store
  ) {}

  ngOnInit(): void {
    this.aclService.CurrentUserHasMinimumRole('CSR1').subscribe(result => {
      this.isCsr = result;
    });
    this.store.selectOnce(ClientSelectionState.clientCode).subscribe(code => {
      this.clientCode = code;
      this._loadUsers();
    });
  }
  public addExistingUser(user: ClientUser) {
    this.openEdit(user);
  }

  public closeModal(invite: UserInvite) {
    if (invite != null) {
      //Modal submitted successfully
      this._modalSubmit(invite);
    } else {
      //Modal Canceled
      this.showInviteModal = false;
      this.inviteModel = null;
      this.isEdit = false;
    }
  }

  public getAccessLevel(clientUser: ClientUser): string {
    if (clientUser.Roles.length == 0) {
      return '';
    }
    if (clientUser.Roles.some(x => x.Name.includes('Client Portal Admin')))
      return 'Client Portal Admin';
    return 'Client Portal User';
  }

  public isCurrentUser(id: string) {
    return id === this.authService.user.UserId;
  }

  public loadPage(pageNumber: number) {
    this.userService
      .searchUsers({
        omniSearch: this.searchQuery,
        page: pageNumber,
        pageSize: this.pagination.PerPage,
        email: null,
        phone: null,
        name: null
      })
      .subscribe((resp: any) => {
        this.searchResults = resp.Items;
        this.pagination = resp.Pagination;
      });
  }
  public openEdit(user: ClientUser) {
    this.inviteModel = user;
    this.showInviteModal = true;
    this.isEdit = true;
  }

  public openModal() {
    this.showInviteModal = true;
  }

  public remove(user: ClientUser) {
    this.modalService.confirmModalCallback.subscribe(val => {
      if (val) this._confirmedRemoval(user);
    });
    this.modalService.showConfirmModal(
      `Are you sure you want to remove username ${user.UserName}?`
    );
  }

  public resendLink(user: ClientUser) {
    this.userService.resendInvite(this.clientCode, user.UserId, user.UserName).subscribe(() => {
      this._loadUsers();
    });
  }
  public unlockUser(user: ClientUser) {
    if (this.canUserBeUnlocked(user)) {
      this.userService.unlockUser(user.UserId, this.clientCode).subscribe(() => {
        this._loadUsers();
      });
    }
  }
  public isUserStatusValid(user: ClientUser) {
    return user.RegistrationStatus != 'Invite Expired' && !user.IsLocked;
  }

  public canUserBeUnlocked(user: ClientUser) {
    if (!user.IsLocked) return false;
    //Only Client Portal Admins or CSRs can get to this screen.
    //Since either of them can unlock password attempts, we don't
    //need a role check here.
    if (user.CanBeUnlockedByNonCsr) {
      return true;
    }
    return this.isCsr;
  }

  private _confirmedRemoval(user: ClientUser) {
    const rolesToRemove = [this.getAccessLevel(user)];
    if (user.Permissions != null) {
      user.Permissions.forEach(permission => {
        rolesToRemove.push(permission);
      });
    }
    this.userService.removeAclJoin(this.clientCode, user.UserId, rolesToRemove).subscribe(() => {
      this._loadUsers();
      if (this.company == null) {
        this.companyService.getCompany(this.clientCode).subscribe(company => {
          this.company = company;
          this._sendUserRemovedNotification(user);
        });
      } else {
        this._sendUserRemovedNotification(user);
      }
    });
  }

  private _inviteNewUser(invite: UserInvite) {
    this.userService.inviteUser(this.clientCode, invite).subscribe(
      () => {
        this.showInviteModal = false;
        this._loadUsers();
      },
      err => {
        const m = FlattenResponseMessages(err.Details, true);
        let msg = `${err.Message}\n` + m.Body;
        this.modalService.showAlertModal(msg, ModalStates.Error);
      }
    );
  }

  private _loadUsers() {
    this.userService.getClientUsers(this.clientCode).subscribe(users => {
      if (users.some(user => this.isCurrentUser(user.UserId))) {
        this.clientUsers = users.sort((a, b) => {
          if (this.isCurrentUser(a.UserId)) {
            return -1;
          } else if (this.isCurrentUser(b.UserId)) {
            return 1;
          }
          return 0;
        });
      } else {
        this.clientUsers = users;
      }
    });
  }

  private _modalSubmit(invite: UserInvite) {
    if (this._validateExistingUser(invite)) {
      this.modalService.showAlertModal(
        'This user already exists. Check the list below to edit their permissions.',
        ModalStates.Error
      );
    } else if (this.inviteModel == null) {
      this._inviteNewUser(invite);
    } else {
      this._updateUserPermissions(invite);
    }
    this.showInviteModal = false;
  }

  private _sendUserRemovedNotification(user) {
    const substitutionFields = new Map<string, string>();
    substitutionFields.set('contactName', `${user.FirstName} ${user.LastName}`);
    substitutionFields.set('companyName', this.company.Name);
    substitutionFields.set('userName', user.UserName);
    substitutionFields.set('addedByName', this.authService.getCurrentUserName());
    this.notificationService
      .sendNotification('ClientAccountRemoved', substitutionFields, this.clientCode)
      .subscribe(() => {});
  }

  private _updateUserPermissions(invite: UserInvite) {
    const observables: Observable<any>[] = [];
    const rolesToAdd = [];
    const rolesToRemove = [];
    if (invite.AccessLevel == 'Client Portal User') {
      if (this.inviteModel.Permissions != null) {
        const removedPermissions = this.inviteModel.Permissions.filter(
          x => !invite.Permissions.includes(x)
        );
        const addedPermissions = invite.Permissions.filter(
          x => !this.inviteModel.Permissions.includes(x)
        );
        rolesToRemove.push(...removedPermissions);
        rolesToAdd.push(...addedPermissions);
      } else {
        rolesToAdd.push(...invite.Permissions);
      }
    }
    if (invite.AccessLevel != this.getAccessLevel(this.inviteModel)) {
      if (this.getAccessLevel(this.inviteModel) != '') {
        rolesToRemove.push(this.getAccessLevel(this.inviteModel));
      }
      rolesToAdd.push(invite.AccessLevel);
    }
    if (rolesToAdd.length > 0) {
      observables.push(
        this.userService.addAclJoin(this.clientCode, this.inviteModel.UserId, rolesToAdd)
      );
    }
    if (rolesToRemove.length > 0) {
      observables.push(
        this.userService.removeAclJoin(this.clientCode, this.inviteModel.UserId, rolesToRemove)
      );
    }
    if (
      invite.IncludeTimekeeping &&
      invite.TimekeepingId &&
      (!this.inviteModel.TimekeepingId || this.inviteModel.TimekeepingId != invite.TimekeepingId)
    ) {
      observables.push(
        this.userService.addUserTimekeepingConfig(
          this.inviteModel.UserId,
          this.clientCode,
          1,
          invite.TimekeepingId
        )
      );
    }
    if (!invite.IncludeTimekeeping && this.inviteModel.TimekeepingId) {
      observables.push(
        this.userService.deleteUserTimekeepingConfig(this.inviteModel.UserId, this.clientCode)
      );
    }

    concat(...observables).subscribe(() => {
      this.showInviteModal = false;
      this.inviteModel = null;
      this.isEdit = false;
      this._loadUsers();
    });
  }
  private _validateExistingUser(invite: UserInvite) {
    //We need to bypass this check on edits, so that it doesn't error out.
    if (this.isEdit) {
      return false;
    }
    const email = this.inviteModel?.UserName ?? invite.Email;
    if (email == null) {
      this.modalService.showAlertModal('No email address provided', ModalStates.Error);
      return false;
    }
    return this.clientUsers.some(x => x.UserName.toLowerCase() === email.toLowerCase());
  }
}
